import {
  calendarDayNow,
  formatStoredDateTime,
  previousCalendarDay,
  storedCalendarDay,
} from '@/shared/utils/displayFormat';
import type { DisplayFormat } from '@/shared/utils/displayFormat';
import type { AppNotification } from '../../../services/notificationsApi';

/**
 * How the panel files a notification: Today, Yesterday, or Earlier.
 *
 * <p>Three buckets and no more, as the reference draws them. A fourth ("This week") would need a rule for
 * where a week starts, which differs by locale and is not configured anywhere in this platform.
 */
export type NotificationGroupLabel = 'Today' | 'Yesterday' | 'Earlier';

export interface NotificationGroup {
  label: NotificationGroupLabel;
  items: NotificationRow[];
}

/** One notification with the two strings the row draws that are not on the record itself. */
export interface NotificationRow {
  notification: AppNotification;
  /** `10:24 AM` today, `Yesterday` yesterday, `12 Oct` before that — as the reference shows it. */
  time: string;
}

const ORDER: NotificationGroupLabel[] = ['Today', 'Yesterday', 'Earlier'];

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The notifications, grouped and labelled, in the order the panel draws them.
 *
 * <h2>Grouped here rather than by the server</h2>
 *
 * <p>Which day a timestamp falls on depends on the reader's zone, and the client is the only side that
 * knows which zone that is for certain — the same row is "today" in Mumbai and "yesterday" in London for
 * three and a half hours every evening. The server sends the instant; this decides what to call it.
 *
 * <p>The server's order is preserved exactly: it sends newest first, every bucket keeps that order, and an
 * empty bucket is dropped rather than drawn as a heading with nothing under it.
 *
 * @param format the entity's date pattern, time pattern and zone; `{}` falls back to the platform's own
 * @param now    the instant "today" is measured from, injectable so a test can pin one
 */
export function groupNotifications(
  items: AppNotification[],
  format: DisplayFormat,
  now: Date = new Date(),
): NotificationGroup[] {
  const today = calendarDayNow(format.zone, now);
  const yesterday = previousCalendarDay(today);

  const buckets = new Map<NotificationGroupLabel, NotificationRow[]>();
  for (const label of ORDER) buckets.set(label, []);

  for (const notification of items) {
    const day = storedCalendarDay(notification.createdAt, format);
    /*
      A timestamp the platform did not write cannot be placed on a day, so it goes under Earlier rather
      than being dropped: the row's own text is what the reader came for, and a notification vanishing
      because its date was odd is worse than one filed a little vaguely.
    */
    const label: NotificationGroupLabel =
      day === null ? 'Earlier' : day === today ? 'Today' : day === yesterday ? 'Yesterday' : 'Earlier';
    buckets.get(label)?.push({ notification, time: timeLabel(notification, format, label) });
  }

  return ORDER.map((label) => ({ label, items: buckets.get(label) ?? [] })).filter(
    (group) => group.items.length > 0,
  );
}

/**
 * What the row shows to the right of its title.
 *
 * <p>Three forms, because they answer three different questions. Today, the clock time is what tells two
 * notifications apart. Yesterday, the clock is noise — the heading already said which day. Earlier, the
 * date is the only thing that locates it, and the year is left off because a bell holding anything older
 * than a year is a retention problem, not a formatting one.
 */
function timeLabel(
  notification: AppNotification,
  format: DisplayFormat,
  label: NotificationGroupLabel,
): string {
  if (label === 'Yesterday') return 'Yesterday';

  const drawn = formatStoredDateTime(notification.createdAt, format);
  /* Not a date the platform writes: show nothing rather than "Invalid Date". */
  if (drawn === null) return '';

  const comma = drawn.indexOf(', ');
  if (label === 'Today') {
    /* `formatStoredDateTime` answers "date, time"; today has room for the time alone. */
    return comma < 0 ? drawn : drawn.slice(comma + 2);
  }
  return shortDate(notification, format, comma < 0 ? drawn : drawn.slice(0, comma));
}

/**
 * `12 Oct`, independent of the entity's date pattern.
 *
 * <p>The pattern is honoured everywhere a date is the subject of the screen. Here it is a caption beside a
 * title, the panel is 460px wide, and `12-10-2026` in that slot pushes the title into two lines for no
 * gain. Falls back to the drawn date where the day cannot be read.
 */
function shortDate(notification: AppNotification, format: DisplayFormat, drawnDate: string): string {
  const day = storedCalendarDay(notification.createdAt, format);
  if (day === null) return drawnDate;
  const parts = day.split('-');
  const month = SHORT_MONTHS[Number(parts[1]) - 1];
  const date = Number(parts[2]);
  if (month === undefined || Number.isNaN(date)) return drawnDate;
  return `${String(date)} ${month}`;
}
